import { posApi } from "../../api/client";
import { getEffectiveLoyaltyBalanceFromCache } from "./loyalty-provisional";
import {
  getCachedAffordableLoyaltyRewards,
  getCachedCustomerByCustomerId,
  getCachedLoyaltyRewardById,
  saveLoyaltyRewardsBatchToReferenceCache,
} from "./reference-cache";
import type { LocalOrderRewardSnapshot } from "./types";
import {
  validateLoyaltyRewardFromCache,
  type LoyaltyRewardCartLine,
} from "./validate-loyalty-reward-from-cache";

export type ResolveLocalFirstLoyaltyRewardPreviewInput = {
  branchId: string;
  customerId: string;
  rewardId: string;
  orderId?: string;
  cartItems?: LoyaltyRewardCartLine[];
  orderGrandTotal?: number;
  validateOnline?: () => ReturnType<typeof posApi.previewLoyaltyReward>;
};

export type ResolveLocalFirstLoyaltyRewardPreviewResult = {
  eligible: boolean;
  error?: string;
  pointsCost: number;
  currentBalance: number;
  balanceAfter: number;
  discountPreview?: number;
  rewardName: string;
  rewardType: string;
  source: "network" | "cache";
  needsServerConfirmation: boolean;
  snapshot?: LocalOrderRewardSnapshot;
};

function canUseNetwork(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}

async function resolveCachedBalance(
  branchId: string,
  customerId: string,
): Promise<number> {
  const record = await getCachedCustomerByCustomerId(branchId, customerId);
  return getEffectiveLoyaltyBalanceFromCache(
    record?.loyaltyPoints,
    branchId,
    customerId,
  );
}

export async function resolveLocalFirstLoyaltyRewardPreview(
  input: ResolveLocalFirstLoyaltyRewardPreviewInput,
): Promise<ResolveLocalFirstLoyaltyRewardPreviewResult> {
  const capturedAt = new Date().toISOString();

  if (canUseNetwork() && input.validateOnline) {
    try {
      const result = await input.validateOnline();
      const cached = await getCachedLoyaltyRewardById(
        input.branchId,
        input.rewardId,
      );
      if (result.eligible) {
        return {
          eligible: true,
          pointsCost: result.pointsCost,
          currentBalance: result.currentBalance,
          balanceAfter: result.balanceAfter,
          discountPreview: result.discountPreview,
          rewardName: result.rewardName,
          rewardType: result.rewardType,
          source: "network",
          needsServerConfirmation: false,
          snapshot: {
            rewardId: input.rewardId,
            rewardName: result.rewardName,
            pointsCost: result.pointsCost,
            fulfillmentType: result.rewardType,
            discountAmount: result.discountPreview ?? 0,
            capturedAt,
            source: "network",
            productVariantId:
              cached?.rewardType === "FREE_PRODUCT"
                ? (cached.fulfillment as { productVariantId?: string })
                    ?.productVariantId
                : undefined,
          },
        };
      }
      return {
        eligible: false,
        error: result.error ?? "ບໍ່ສາມາດແລກລາງວັນນີ້",
        pointsCost: result.pointsCost,
        currentBalance: result.currentBalance,
        balanceAfter: result.balanceAfter,
        rewardName: result.rewardName,
        rewardType: result.rewardType,
        source: "network",
        needsServerConfirmation: false,
      };
    } catch {
      // fall through to cache
    }
  }

  const reward = await getCachedLoyaltyRewardById(
    input.branchId,
    input.rewardId,
  );
  if (!reward) {
    return {
      eligible: false,
      error: canUseNetwork()
        ? "ບໍ່ພົບລາງວັນໃນ cache"
        : "ບໍ່ພົບລາງວັນໃນ cache — ກະລຸນາ refresh cache ຫຼືເຊື່ອມຕໍ່ອິນເຕີເນັດ",
      pointsCost: 0,
      currentBalance: 0,
      balanceAfter: 0,
      rewardName: "",
      rewardType: "",
      source: "cache",
      needsServerConfirmation: canUseNetwork(),
    };
  }

  const effectiveBalance = await resolveCachedBalance(
    input.branchId,
    input.customerId,
  );
  const cacheResult = validateLoyaltyRewardFromCache({
    reward,
    effectiveBalance,
    orderGrandTotal: input.orderGrandTotal,
    cartItems: input.cartItems,
    at: capturedAt,
  });

  if (!cacheResult.valid) {
    return {
      eligible: false,
      error: cacheResult.error,
      pointsCost: cacheResult.pointsCost,
      currentBalance: effectiveBalance,
      balanceAfter: cacheResult.balanceAfter ?? effectiveBalance,
      rewardName: reward.name,
      rewardType: reward.rewardType,
      source: "cache",
      needsServerConfirmation: canUseNetwork(),
    };
  }

  return {
    eligible: true,
    pointsCost: cacheResult.pointsCost,
    currentBalance: effectiveBalance,
    balanceAfter: cacheResult.balanceAfter ?? effectiveBalance,
    discountPreview: cacheResult.discountAmount,
    rewardName: reward.name,
    rewardType: reward.rewardType,
    source: "cache",
    needsServerConfirmation: canUseNetwork(),
    snapshot: {
      rewardId: reward.id,
      rewardName: reward.name,
      pointsCost: cacheResult.pointsCost,
      fulfillmentType: reward.rewardType,
      discountAmount: cacheResult.discountAmount ?? 0,
      capturedAt,
      source: "cache",
      productVariantId:
        reward.rewardType === "FREE_PRODUCT"
          ? (reward.fulfillment as { productVariantId?: string })
              ?.productVariantId
          : undefined,
    },
  };
}

export async function listLocalFirstAffordableRewards(input: {
  branchId: string;
  customerId: string;
  cartItems?: LoyaltyRewardCartLine[];
  orderGrandTotal?: number;
}): Promise<{
  rewards: Array<{
    id: string;
    name: string;
    description: string | null;
    imageUrl: string | null;
    rewardType: string;
    pointsCost: number;
    fulfillment: unknown;
    sortOrder: string;
  }>;
  balance: number;
  fromCache: boolean;
}> {
  if (canUseNetwork()) {
    try {
      const [api, cachedBalance] = await Promise.all([
        posApi.getEligibleLoyaltyRewards(input.customerId, true),
        resolveCachedBalance(input.branchId, input.customerId),
      ]);
      const balance = api.balance ?? cachedBalance;

      if (api.rewards.length > 0) {
        await saveLoyaltyRewardsBatchToReferenceCache(
          input.branchId,
          api.rewards,
        );
        return {
          rewards: api.rewards,
          balance,
          fromCache: false,
        };
      }

      const fromCache = await getCachedAffordableLoyaltyRewards({
        branchId: input.branchId,
        effectiveBalance: balance,
      });
      if (fromCache.length > 0) {
        return { rewards: fromCache, balance, fromCache: true };
      }

      return { rewards: [], balance, fromCache: false };
    } catch {
      // cache fallback
    }
  }

  const balance = await resolveCachedBalance(
    input.branchId,
    input.customerId,
  );
  const rewards = await getCachedAffordableLoyaltyRewards({
    branchId: input.branchId,
    effectiveBalance: balance,
  });
  return { rewards, balance, fromCache: true };
}
